// components/LibraryComponents.jsx
import { useState, useEffect } from "react";
import {
  librarySeatsList,
  libraryStudentList,
  libraryFeeCollection,
  libraryFeeDepositInfov2,
  libraryFeeDepositv2,
  libraryFeeReceiptv2,
} from "../../../api/Library";
import { BankList } from "../../../api/BankService";

// ════════════════════════════════════════════════════════════════
//  FeeDepositModal
// ════════════════════════════════════════════════════════════════
export function FeeDepositModal({ student, libraryId, onClose, onSuccess }) {
  const [banks, setBanks] = useState([]);
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [receipt, setReceipt] = useState(null); // transaction for receipt view
  const [form, setForm] = useState({
    amount: "",
    method: "Cash",
    payment_date: new Date().toISOString().slice(0, 10),
    transaction_no: "",
    bank_id: "",
    remark: "",
  });
  const [err, setErr] = useState("");

  useEffect(() => {
    const qs = `id=${student.library_student_id}&library_id=${student.library_id}`;
    libraryFeeDepositInfov2(qs).then((r) => {
      if (r?.data?.status) setInfo(r.data.data);
    });

    BankList()
      .then((r) => {
        if (r?.data?.status) setBanks(r.data.data ?? []);
      })
      .catch(() => {});
  }, [student]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const inr = (v) => "₹" + Number(v || 0).toLocaleString("en-IN");

  const handleSubmit = async () => {
    if (!form.amount || !form.bank_id || !form.payment_date) {
      setErr("Amount, bank and date are required.");
      return;
    }
    setLoading(true);
    setErr("");
    try {
      const res = await libraryFeeDepositv2({
        id: student.library_student_id,
        student_id: student.student_id,
        library_id: student.library_id,
        amount: parseFloat(form.amount),
        method: form.method,
        payment_date: form.payment_date,
        transaction_no: form.transaction_no,
        bank_id: form.bank_id,
        remark: form.remark,
      });
      if (res?.data?.status) {
        // fetch receipt then show it; parent reload happens on receipt close
        const txnId = res.data.transaction_id;
        if (txnId) {
          const rcRes = await libraryFeeReceiptv2(txnId);
          if (rcRes?.data?.status) {
            setReceipt({ data: rcRes.data.data, school: rcRes.data.school });
            return; // stay open showing receipt
          }
        }
        onSuccess();
      } else {
        setErr(
          Array.isArray(res?.data?.message)
            ? res.data.message.join(", ")
            : (res?.data?.message ?? "Payment failed."),
        );
      }
    } catch (e) {
      setErr(e?.response?.data?.message ?? "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  // ── Receipt view after successful payment ─────────────────
  if (receipt) {
    return (
      <ReceiptView
        data={receipt.data}
        school={receipt.school}
        onClose={() => {
          setReceipt(null);
          onSuccess();
        }}
      />
    );
  }

  return (
    <div
      className="modal fade show d-block"
      tabIndex="-1"
      style={{ background: "rgba(0,0,0,.5)" }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          {/* Header */}
          <div className="modal-header bg-label-primary py-3">
            <div>
              <h5 className="modal-title mb-0">Collect Fee</h5>
              <small className="text-muted">{student.student_name}</small>
            </div>
            <button type="button" className="btn-close" onClick={onClose} />
          </div>

          {/* Outstanding summary */}
          {info && (
            <div className="d-flex gap-4 px-3 py-2 bg-light border-bottom">
              <div>
                <small className="text-muted d-block">Total Due</small>
                <span className="fw-semibold text-danger">
                  {inr(info.total_due ?? 0)}
                </span>
              </div>
              <div>
                <small className="text-muted d-block">Advance Balance</small>
                <span className="fw-semibold text-success">
                  {inr(info.advance_fee ?? 0)}
                </span>
              </div>
              {info.last_transaction && (
                <div>
                  <small className="text-muted d-block">Last Payment</small>
                  <span className="fw-semibold">
                    {inr(info.last_transaction.amount)} &middot;{" "}
                    {info.last_transaction.payment_date}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Form */}
          <div className="modal-body">
            <div className="row g-3">
              <div className="col-12 form-floating form-floating-outline">
                <input
                  type="number"
                  className="form-control"
                  placeholder="Amount"
                  min="1"
                  value={form.amount}
                  onChange={(e) => set("amount", e.target.value)}
                />
                <label>Amount *</label>
              </div>

              <div className="col-6 form-floating form-floating-outline">
                <select
                  className="form-select"
                  value={form.method}
                  onChange={(e) => set("method", e.target.value)}
                >
                  <option>Cash</option>
                  <option>Online</option>
                </select>
                <label>Payment Method</label>
              </div>
              <div className="col-6 form-floating form-floating-outline">
                <select
                  className="form-select"
                  value={form.bank_id}
                  onChange={(e) => set("bank_id", e.target.value)}
                >
                  <option value="">Select Bank *</option>
                  {banks.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.account_no} - {b.branch} ({b.amount})
                    </option>
                  ))}
                </select>
                <label>Bank *</label>
              </div>

              <div className="col-6 form-floating form-floating-outline">
                <input
                  type="date"
                  className="form-control"
                  value={form.payment_date}
                  onChange={(e) => set("payment_date", e.target.value)}
                />
                <label>Payment Date *</label>
              </div>

              <div className="col-6 form-floating form-floating-outline">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Transaction No"
                  value={form.transaction_no}
                  onChange={(e) => set("transaction_no", e.target.value)}
                />
                <label>Transaction No</label>
              </div>

              <div className="col-12 form-floating form-floating-outline">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Remark"
                  value={form.remark}
                  onChange={(e) => set("remark", e.target.value)}
                />
                <label>Remark</label>
              </div>

              {err && (
                <div className="col-12">
                  <div className="alert alert-danger py-2 mb-0">{err}</div>
                </div>
              )}
            </div>
          </div>

          <div className="modal-footer bg-label-primary py-2">
            <button
              className="btn btn-danger btn-sm"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              className="btn btn-primary px-4"
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading && (
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                />
              )}
              {loading ? "Processing…" : "Confirm Payment"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
//  ReceiptView  (shown inside FeeDepositModal after success)
// ════════════════════════════════════════════════════════════════
function ReceiptView({ data, school, onClose }) {
  const inr = (v) => "₹" + Number(v || 0).toLocaleString("en-IN");

  const handlePrint = () => {
    const el = document.getElementById("lib-fee-receipt-content");
    const win = window.open("", "_blank");
    win.document.write(`
      <html><head><title>Library Fee Receipt</title>
      <style>
        body{font-family:Arial,sans-serif;font-size:13px;padding:20px}
        table{width:100%;border-collapse:collapse}
        td{padding:6px 8px;border:1px solid #ddd}
        .label{background:#f5f5f5;font-weight:bold;width:40%}
        .center{text-align:center}
        .success{color:#1baf7a;font-weight:600}
      </style></head><body>
      ${el.innerHTML}
      </body></html>
    `);
    win.document.close();
    win.print();
    win.close();
  };

  const rows = [
    ["Receipt No", `#${data?.id}`],
    ["Student", data?.library_student?.student_name],
    ["Mobile", data?.library_student?.mobile],
    ["Library", data?.library_student?.library?.name],
    ["Seat No", data?.library_student?.seat?.seat_no],
    ["Shift", data?.library_student?.shift?.name],
    ["Amount Paid", inr(data?.amount)],
    ["Method", data?.payment_method],
    ["Transaction No", data?.transaction_no],
    ["Bank", data?.bank?.name],
    ["Payment Date", data?.payment_date],
    ["Remark", data?.remark],
  ].filter(([, v]) => v);

  return (
    <div
      className="modal fade show d-block"
      tabIndex="-1"
      style={{ background: "rgba(0,0,0,.5)" }}
    >
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header bg-label-success py-3">
            <h5 className="modal-title">
              <i className="ri-checkbox-circle-line text-success me-2"></i>
              Payment Successful
            </h5>
            <button type="button" className="btn-close" onClick={onClose} />
          </div>

          <div className="modal-body" id="lib-fee-receipt-content">
            <div className="text-center mb-3">
              {school?.logo && (
                <img
                  src={school.logo}
                  alt="logo"
                  style={{ height: 48, marginBottom: 6 }}
                />
              )}
              <h6 className="mb-0">{school?.name}</h6>
              <small className="text-muted">{school?.address}</small>
              <div className="border-top border-dashed my-2" />
              <strong>Library Fee Receipt</strong>
            </div>

            <table className="table table-bordered table-sm mb-0">
              <tbody>
                {rows.map(([label, val]) => (
                  <tr key={label}>
                    <td
                      className="fw-semibold bg-light"
                      style={{ width: "40%" }}
                    >
                      {label}
                    </td>
                    <td
                      className={
                        label === "Amount Paid"
                          ? "fw-semibold text-success"
                          : ""
                      }
                    >
                      {val}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p
              className="text-center text-muted mt-2 mb-0"
              style={{ fontSize: 12 }}
            >
              Thank you for your payment!
            </p>
          </div>

          <div className="modal-footer py-2">
            <button
              className="btn btn-outline-secondary btn-sm"
              onClick={onClose}
            >
              Close
            </button>
            <button className="btn btn-primary btn-sm" onClick={handlePrint}>
              <i className="ri-printer-line me-1"></i> Print Receipt
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
//  LibraryStatCards
// ════════════════════════════════════════════════════════════════
export function LibraryStatCards({ stats, loading }) {
  if (loading) {
    return (
      <div className="row mb-1">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="col-sm-6 col-lg-3 mb-3">
            <div
              className="card border-0 shadow-sm"
              style={{ height: 90, opacity: 0.5 }}
            />
          </div>
        ))}
      </div>
    );
  }

  if (!stats) return null;

  const inr = (v) => "₹" + Number(v || 0).toLocaleString("en-IN");
  const sign = (v) => (v > 0 ? `+${v}%` : `${v}%`);

  const cards = [
    {
      label: "Total Seats",
      val: stats.seats.total,
      sub: `${stats.seats.occupancy_rate}% occupied`,
      icon: "ri-layout-grid-line",
      bg: "bg-label-primary",
      subClass: "",
    },
    {
      label: "Active Students",
      val: stats.students.active,
      sub: `${stats.students.inactive} inactive`,
      icon: "ri-graduation-cap-line",
      bg: "bg-label-success",
      subClass: "",
    },
    {
      label: "Monthly Revenue",
      val: inr(stats.revenue.current_month),
      sub: `${sign(stats.revenue.change_pct)} vs last month`,
      icon: "ri-money-rupee-circle-line",
      bg: "bg-label-info",
      subClass: stats.revenue.change_pct >= 0 ? "text-success" : "text-danger",
    },
    {
      label: "Total Outstanding",
      val: inr(stats.dues.total_outstanding),
      sub: `${stats.dues.overdue_students} students overdue`,
      icon: "ri-error-warning-line",
      bg: "bg-label-danger",
      subClass: "text-danger",
    },
  ];

  return (
    <div className="row mb-1">
      {cards.map((c, i) => (
        <div key={i} className="col-sm-6 col-lg-3 mb-3">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body d-flex align-items-center">
              <div
                className={`avatar ${c.bg} rounded me-3 d-flex align-items-center justify-content-center`}
                style={{ width: 60, height: 60, minWidth: 60 }}
              >
                <i className={`${c.icon} fs-3`}></i>
              </div>
              <div>
                <small className="text-muted">{c.label}</small>
                <h4 className="mb-0">{c.val}</h4>
                <small className={c.subClass || "text-muted"}>{c.sub}</small>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
//  SeatMap
// ════════════════════════════════════════════════════════════════
export function SeatMap({ libraryId }) {
  const [seats, setSeats] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const qs = `limit=all${libraryId ? `&library_id=${libraryId}` : ""}`;
    librarySeatsList(qs)
      .then((r) => {
        if (r?.data?.status) {
          const d = r.data.data;
          setSeats(Array.isArray(d) ? d : (d?.data ?? []));
        }
      })
      .finally(() => setLoading(false));
  }, [libraryId]);

  const countBy = (s) => seats.filter((x) => x.status === s).length;

  const seatClass = (status) => {
    if (status === "Occupied") return "bg-label-danger";
    if (status === "Vacant") return "bg-label-success";
    return "bg-label-secondary";
  };

  return (
    <div className="card border-0 shadow-sm">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="mb-0">Seat Map</h5>
          <div className="d-flex gap-3">
            <small className="text-muted">
              <i className="ri-checkbox-blank-circle-fill text-danger me-1"></i>
              Occupied {countBy("Occupied")}
            </small>
            <small className="text-muted">
              <i className="ri-checkbox-blank-circle-fill text-success me-1"></i>
              Vacant {countBy("Vacant")}
            </small>
            <small className="text-muted">
              <i className="ri-checkbox-blank-circle-fill text-secondary me-1"></i>
              Out of order {countBy("Out of order")}
            </small>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-4">
            <div className="spinner-border text-primary" role="status" />
          </div>
        ) : seats.length === 0 ? (
          <div className="text-center py-4">
            <i className="ri-layout-grid-line fs-1 text-muted"></i>
            <p className="text-muted mt-2 mb-0">No seats found</p>
          </div>
        ) : (
          <div
            className="d-flex flex-wrap gap-2"
            style={{ maxHeight: 420, overflowY: "auto" }}
          >
            {seats.map((seat) => (
              <div
                key={seat.id}
                className={`badge ${seatClass(seat.status)} d-flex align-items-center justify-content-center`}
                style={{
                  width: 48,
                  height: 40,
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: "default",
                }}
                title={`Seat ${seat.seat_no} — ${seat.status}`}
              >
                {seat.seat_no}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
//  StudentTable
// ════════════════════════════════════════════════════════════════
export function StudentTable({ libraryId }) {
  const [students, setStudents] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams({ page });
    if (libraryId) params.set("library_id", libraryId);
    if (search) params.set("search", search);

    libraryStudentList(params.toString())
      .then((r) => {
        if (r?.data?.status) {
          setStudents(r.data.data.data ?? []);
          setMeta({
            current_page: r.data.data.current_page,
            last_page: r.data.data.last_page,
            total: r.data.data.total,
          });
        }
      })
      .finally(() => setLoading(false));
  }, [libraryId, search, page]);

  const inr = (v) => "₹" + Number(v || 0).toLocaleString("en-IN");
  const initials = (n) =>
    n
      ?.split(" ")
      .map((x) => x[0])
      .join("")
      .slice(0, 2)
      .toUpperCase() ?? "";

  return (
    <div className="card border-0 shadow-sm">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
          <h5 className="mb-0">
            Students{" "}
            {meta && (
              <span className="badge bg-label-primary ms-1">{meta.total}</span>
            )}
          </h5>
          <input
            className="form-control"
            style={{ width: 260 }}
            placeholder="Search name, mobile, address…"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
        </div>

        {loading ? (
          <div className="text-center py-4">
            <div className="spinner-border text-primary" role="status" />
          </div>
        ) : (
          <>
            <div className="table-responsive">
              <table className="table table-hover align-middle">
                <thead className="table-light">
                  <tr>
                    <th>Student</th>
                    <th>Reg No</th>
                    <th>Seat</th>
                    <th>Shift</th>
                    <th>Fee Type</th>
                    <th className="text-end">Final Fee</th>
                    <th>Joined</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {students.length === 0 ? (
                    <tr>
                      <td colSpan="8">
                        <div className="text-center py-4">
                          <i className="ri-user-line fs-1 text-muted"></i>
                          <h6 className="mt-2">No students found</h6>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    students.map((s) => (
                      <tr key={s.id}>
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            <div
                              className="rounded-circle bg-label-primary d-flex align-items-center justify-content-center"
                              style={{
                                width: 36,
                                height: 36,
                                minWidth: 36,
                                fontSize: 12,
                                fontWeight: 600,
                              }}
                            >
                              {initials(s.student_name)}
                            </div>
                            <div>
                              <div className="fw-semibold">
                                {s.student_name}
                              </div>
                              <small className="text-muted">{s.mobile}</small>
                            </div>
                          </div>
                        </td>
                        <td className="text-muted">
                          {s.registration_no || "—"}
                        </td>
                        <td>{s.seat?.seat_no ?? "—"}</td>
                        <td>
                          <span className="badge bg-label-info">
                            {s.shift?.name ?? "—"}
                          </span>
                        </td>
                        <td>{s.fee_type}</td>
                        <td className="text-end">{inr(s.final_fee)}</td>
                        <td className="text-muted">{s.joining_date}</td>
                        <td>
                          <span
                            className={`badge ${s.status === "1" ? "bg-label-success" : "bg-label-secondary"}`}
                          >
                            {s.status === "1" ? "Active" : "Inactive"}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {meta && meta.last_page > 1 && (
              <div className="d-flex justify-content-between align-items-center mt-3">
                <small className="text-muted">
                  Page {meta.current_page} of {meta.last_page} ({meta.total}{" "}
                  students)
                </small>
                <div className="d-flex gap-1">
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => p - 1)}
                  >
                    <i className="ri-arrow-left-s-line"></i>
                  </button>
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    disabled={page >= meta.last_page}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    <i className="ri-arrow-right-s-line"></i>
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
//  FeeCollectionTable
// ════════════════════════════════════════════════════════════════
export function FeeCollectionTable({ libraryId, refreshKey = 0, onRefresh }) {
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [month, setMonth] = useState("");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);

  const monthOptions = (() => {
    const opts = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      opts.push({
        label: d.toLocaleString("default", { month: "long", year: "numeric" }),
        value: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`,
      });
    }
    return opts;
  })();

  const load = () => {
    setLoading(true);
    const params = new URLSearchParams({ page });
    if (libraryId) params.set("library_id", libraryId);
    if (month) params.set("month", month);
    if (search) params.set("search", search);

    libraryFeeCollection(params.toString())
      .then((r) => {
        if (r?.data?.status) {
          setRows(r.data.data.data ?? []);
          setMeta({
            current_page: r.data.data.current_page,
            last_page: r.data.data.last_page,
            total: r.data.data.total,
          });
        }
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, [libraryId, month, search, page, refreshKey]);

  const inr = (v) => "₹" + Number(v || 0).toLocaleString("en-IN");
  const badgeClass = (row) => {
    if (row.left_amount <= 0) return "bg-label-success";
    if (row.paid_amount > 0) return "bg-label-warning";
    return "bg-label-danger";
  };
  const badgeLabel = (row) => {
    if (row.left_amount <= 0) return "Paid";
    if (row.paid_amount > 0) return "Partial";
    return "Pending";
  };

  return (
    <div className="card border-0 shadow-sm">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
          <h5 className="mb-0">
            Fee Collection{" "}
            {meta && (
              <span className="badge bg-label-primary ms-1">{meta.total}</span>
            )}
          </h5>
          <div className="d-flex gap-2 flex-wrap">
            <select
              className="form-select"
              style={{ width: 200 }}
              value={month}
              onChange={(e) => {
                setMonth(e.target.value);
                setPage(1);
              }}
            >
              <option value="">All Months</option>
              {monthOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            <input
              className="form-control"
              style={{ width: 220 }}
              placeholder="Search student…"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />
          </div>
        </div>

        {loading ? (
          <div className="text-center py-4">
            <div className="spinner-border text-primary" role="status" />
          </div>
        ) : (
          <>
            <div className="table-responsive">
              <table className="table table-hover align-middle">
                <thead className="table-light">
                  <tr>
                    <th>#</th>
                    <th>Student</th>
                    <th>Month</th>
                    <th className="text-end">Billed</th>
                    <th className="text-end">Paid</th>
                    <th className="text-end">Due</th>
                    <th>Status</th>
                    <th className="text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td colSpan="8">
                        <div className="text-center py-4">
                          <i className="ri-receipt-line fs-1 text-muted"></i>
                          <h6 className="mt-2">No records found</h6>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    rows.map((r, idx) => (
                      <tr key={r.id}>
                        <td className="text-muted">
                          {(page - 1) * 20 + idx + 1}
                        </td>
                        <td>
                          <div className="fw-semibold">
                            {r.library_student?.student_name}
                          </div>
                          <small className="text-muted">
                            {r.library_student?.mobile}
                          </small>
                        </td>
                        <td className="text-muted">{r.month}</td>
                        <td className="text-end">{inr(r.final_amount)}</td>
                        <td className="text-end text-success">
                          {inr(r.paid_amount)}
                        </td>
                        <td className="text-end text-danger">
                          {inr(r.left_amount)}
                        </td>
                        <td>
                          <span className={`badge ${badgeClass(r)}`}>
                            {badgeLabel(r)}
                          </span>
                        </td>
                        <td className="text-center">
                          {r.left_amount > 0 && (
                            <button
                              className="btn btn-sm btn-label-primary"
                              onClick={() =>
                                setSelected({
                                  library_student_id: r.library_student_id,
                                  student_name: r.library_student?.student_name,
                                  student_id: r.student_id,
                                  library_id: r.library_id,
                                })
                              }
                            >
                              <i className="ri-money-rupee-circle-line me-1"></i>
                              Collect
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {meta && meta.last_page > 1 && (
              <div className="d-flex justify-content-between align-items-center mt-3">
                <small className="text-muted">
                  Page {meta.current_page} of {meta.last_page} ({meta.total}{" "}
                  records)
                </small>
                <div className="d-flex gap-1">
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => p - 1)}
                  >
                    <i className="ri-arrow-left-s-line"></i>
                  </button>
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    disabled={page >= meta.last_page}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    <i className="ri-arrow-right-s-line"></i>
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {selected && (
        <FeeDepositModal
          student={selected}
          libraryId={libraryId}
          onClose={() => setSelected(null)}
          onSuccess={() => {
            setSelected(null);
            load();
            onRefresh?.();
          }}
        />
      )}
    </div>
  );
}
